import React, { useState } from 'react';
import { Category, Exam } from '../../types';

// Import sub-components
import AdminOverview from './AdminOverview';
import AdminUsers from './AdminUsers';
import AdminCategories from './AdminCategories';
import AdminPapers from './AdminPapers';
import AdminQuestionBank from './AdminQuestionBank';
import AdminCSVUpload from './AdminCSVUpload';
import AdminAIGenerator from './AdminAIGenerator';
import AdminReports from './AdminReports';

interface AdminLayoutProps {
    categories: Category[];
    exams: Exam[];
    onAddCategory: (category: Category) => void;
    onUpdateCategory: (category: Category) => void;
    onDeleteCategory: (categoryId: string) => void;
    onAddExam: (exam: Exam) => void;
    onUpdateExam: (exam: Exam) => void;
    onDeleteExam: (examId: string) => void;
}

type AdminTab = 'overview' | 'users' | 'categories' | 'papers' | 'qbank' | 'csv' | 'ai' | 'reports';

const AdminLayout: React.FC<AdminLayoutProps> = ({
    categories,
    exams,
    onAddCategory,
    onUpdateCategory,
    onDeleteCategory,
    onAddExam,
    onUpdateExam,
    onDeleteExam
}) => {
    const [activeTab, setActiveTab] = useState<AdminTab>('overview');

    const tabs = [
        { id: 'overview', label: 'Overview', icon: 'fa-chart-pie' },
        { id: 'users', label: 'Manage Users', icon: 'fa-users' },
        { id: 'categories', label: 'Categories', icon: 'fa-th-large' },
        { id: 'papers', label: 'Papers', icon: 'fa-file-alt' },
        { id: 'qbank', label: 'Question Bank', icon: 'fa-database' },
        { id: 'csv', label: 'CSV Upload', icon: 'fa-file-csv' },
        { id: 'ai', label: 'AI Generator', icon: 'fa-magic' },
        { id: 'reports', label: 'Reports', icon: 'fa-flag' },
    ];

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'overview':
                return (
                    <AdminOverview
                        categoriesCount={categories.length}
                        onNavigateToTab={(tab) => setActiveTab(tab as AdminTab)}
                    />
                );
            case 'users':
                return <AdminUsers categories={categories} />;
            case 'categories':
                return (
                    <AdminCategories
                        categories={categories}
                        onAddCategory={onAddCategory}
                        onUpdateCategory={onUpdateCategory}
                        onDeleteCategory={onDeleteCategory}
                    />
                );
            case 'papers':
                return (
                    <AdminPapers
                        categories={categories}
                        exams={exams}
                        onAddExam={onAddExam}
                        onUpdateExam={onUpdateExam}
                        onDeleteExam={onDeleteExam}
                    />
                );
            case 'qbank':
                return (
                    <AdminQuestionBank
                        categories={categories}
                        exams={exams}
                        onUpdateExam={onUpdateExam}
                    />
                );
            case 'csv':
                return (
                    <AdminCSVUpload
                        categories={categories}
                        exams={exams}
                        onAddExam={onAddExam}
                        onUpdateExam={onUpdateExam}
                    />
                );
            case 'ai':
                return <AdminAIGenerator />;
            case 'reports':
                return <AdminReports />;
            default:
                return null;
        }
    };

    return (
        <div className="p-8 max-w-7xl mx-auto min-h-screen bg-transparent">
            <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Admin Dashboard</h1>
                <div className="flex bg-white dark:bg-slate-800 rounded-lg p-1 shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto max-w-full">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as AdminTab)}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center whitespace-nowrap ${activeTab === tab.id
                                ? 'bg-orange-500 text-white shadow-sm'
                                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                                }`}
                        >
                            <i className={`fas ${tab.icon} mr-2`}></i>
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {renderActiveTab()}
        </div>
    );
};

export default AdminLayout;
